import {
  type ApiToken,
  type ApiTokenCreated,
  type CreateApiToken,
  apiTokenCreatedSchema,
  apiTokenListSchema,
} from '@xphoto/shared';
import { apiFetch } from '../lib/http';

/**
 * Client typé des CLÉS D'API externes read-only (T8, XPHOTO-13).
 *
 * Les réponses sont RE-PARSÉES par les schémas partagés (never trust the client).
 * Le jeton BRUT n'est renvoyé qu'à la création (`ApiTokenCreated.secret`), une
 * seule fois. Session same-origin (cookie) via `apiFetch` — aucun jeton manipulé
 * ici pour l'auth de la SPA elle-même.
 */

export class ApiKeysApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'ApiKeysApiError';
  }
}

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON (ou 204) : code générique.
  }
  return 'UNKNOWN';
}

/**
 * `GET /api/api-tokens` — liste les clés du compte courant. `signal` permet à
 * l'appelant d'ANNULER un chargement (démontage, ou rechargement plus récent qui
 * doit primer) — sans quoi une réponse lente peut écraser une liste plus fraîche.
 */
export async function fetchApiTokens(signal?: AbortSignal): Promise<ApiToken[]> {
  const res = await apiFetch('/api/api-tokens', { signal: signal ?? null });
  if (!res.ok) throw new ApiKeysApiError(res.status, await errorCodeOf(res));
  return apiTokenListSchema.parse(await res.json()).tokens;
}

/**
 * `POST /api/api-tokens` — crée une clé. Renvoie l'item + le jeton BRUT (à
 * afficher UNE SEULE FOIS ; jamais ré-affichable ensuite).
 */
export async function createApiToken(label: string): Promise<ApiTokenCreated> {
  const payload: CreateApiToken = { label };
  const res = await apiFetch('/api/api-tokens', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new ApiKeysApiError(res.status, await errorCodeOf(res));
  return apiTokenCreatedSchema.parse(await res.json());
}

/** `DELETE /api/api-tokens/:id` — révoque (soft) une clé : elle reste listée « Révoquée ». */
export async function revokeApiToken(id: string): Promise<void> {
  const res = await apiFetch(`/api/api-tokens/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new ApiKeysApiError(res.status, await errorCodeOf(res));
}

/**
 * `DELETE /api/api-tokens/:id/permanent` — supprime DÉFINITIVEMENT une clé (hard
 * delete) : elle disparaît de la liste. Réservé aux clés déjà révoquées côté UI.
 */
export async function deleteApiToken(id: string): Promise<void> {
  const res = await apiFetch(`/api/api-tokens/${id}/permanent`, { method: 'DELETE' });
  if (!res.ok) throw new ApiKeysApiError(res.status, await errorCodeOf(res));
}
